import React from 'react';
import PropTypes from 'prop-types';
import {ErrorBoundary} from 'react-error-boundary';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { translate } from "@flow-frontend-npm/common";
import t from "./translations";

/**
 * Simple MUI-based error-boundary rendering a generic error message
 * that can be expanded to show the (probably very technical) internal error.
 * It also logs the error to the console.
 * @return {node}
 */
const MuiErrorBoundary = ({children}) => (
    <ErrorBoundary
        FallbackComponent={MuiErrorDetails}
        onError={logError}
    >{children}</ErrorBoundary>
);
MuiErrorBoundary.propTypes = {
    children: PropTypes.node
};
/**
 * Renders generic error message and also allows user to see the technical error message.
 * @param {object} error
 * @return {node}
 * @constructor
 */
export const MuiErrorDetails = ({error}) => (
    <Accordion role="alert">
        <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
        >
            <GenericError />
        </AccordionSummary>
        <AccordionDetails>
            <Typography sx={{fontFamily: 'monospace', whitespace: 'preserve'}}>{error.message}</Typography>
        </AccordionDetails>
    </Accordion>
);
MuiErrorDetails.propTypes = {
    error: PropTypes.shape({
        message: PropTypes.string.isRequired
    }).isRequired
};

/**
 * Renders generic error message.
 * @return {*}
 * @constructor
 */
export const GenericError = () => (
    <Typography
        sx={{
            color: ({ palette }) => palette.error.dark,
        }}
    >
        {translate(t, "error.generic")}
    </Typography>
);

/**
 * Does what it says.
 * @param {object} error
 * @param {*} info
 */
const logError = (error, info) => {
    console && console.error && console.error(error, info);
};

export default MuiErrorBoundary;
